import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import {
  TrackingLinksService,
  ListLinkItem,
  LinkDomainItem,
  SetLinkDomainPayload,
  SetLinkDomainResponse,
} from '../../../services/tracking-links/tracking-links.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

export interface TrackingLinkRow {
  id: string;
  alias: string;
  accessCode: string;
  trackingLink: string;
  target: string;
  dateAdded: string;
  time: string;
  notes: string;
  isSelected: boolean;
}

@Component({
  selector: 'app-generate-tracking-link',
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective,
    PaginatorComponent, CreditsLabelPipe],
  templateUrl: './generate-tracking-link.component.html',
  styleUrl: './generate-tracking-link.component.css'
})
export class GenerateTrackingLinkComponent implements OnInit {
  availableCredits: number | null = null;
  creditsLoading = true;
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;
  hasActiveLinkSubscription: boolean = true;
  linkShortenerPermission: boolean = true;

  destinationUrl: string = '';
  targetName: string = '';
  showAdvancedSettings: boolean = false;

  /** Tracking features (GPS & Camera on by default per note) */
  gpsLocation: boolean = true;
  cameraAccess: boolean = true;
  screenshot: boolean = false;
  continuousGpsTracking: boolean = false;

  /** Link creation state */
  createLinkLoading: boolean = false;
  createLinkError: string | null = null;

  /** Success popup: show after link created, with tracking link, destination URL, target name, id */
  showSuccessPopup: boolean = false;
  createdLinkForPopup: {
    id: string;
    trackingLink: string;
    qrCodeDataUrl?: string;
    destinationUrl: string;
    targetName: string;
  } | null = null;
  copyFeedback: boolean = false;
  private copyFeedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Change domain in success popup (no Apply – domain updates on select) */
  linkDomains: LinkDomainItem[] = [];
  linkDomainsLoading = false;
  popupSelectedDomain: string = '';
  setDomainLoading = false;
  setDomainError: string | null = null;

  /** Extra options in popup (Extension, Path, Parameter – select or type custom) */
  popupSelectedExtension: string = '';
  popupSelectedPath: string = '';
  popupSelectedParameter: string = '';
  /** Which combo dropdown is open */
  openComboDropdown: 'domain' | 'extension' | 'path' | 'parameter' | null = null;
  private applyCustomizationDebounce: ReturnType<typeof setTimeout> | null = null;
  /** Preset options for combo dropdowns */
  extensionOptions: string[] = [];
  pathOptions: string[] = [];
  parameterOptions: string[] = [];

  /** List from API GET /api/links */
  trackingLinksData: TrackingLinkRow[] = [];
  listLoading: boolean = false;
  listError: string | null = null;

  /** Delete state */
  deleteLoading: boolean = false;
  deleteError: string | null = null;

  /** Filter state */
  selectedFilterType: string = 'tracking-link';
  filterTypes: Array<{ label: string; value: string }> = [
    { label: 'Tracking Link', value: 'tracking-link' },
    { label: 'Target', value: 'target' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];
  trackingLinkFilter: string = '';
  targetFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  /** Pagination */
  trackingLinksPerPage = 5;
  trackingLinksCurrentPage = 1;
  trackingLinksSelectAll = { isSelected: false };

  /** Filtered list (client-side filter) */
  get trackingLinksFiltered(): TrackingLinkRow[] {
    let list = [...this.trackingLinksData];
    const linkQ = this.trackingLinkFilter.trim().toLowerCase();
    const targetQ = this.targetFilter.trim().toLowerCase();
    const notesQ = this.notesFilter.trim().toLowerCase();
    if (linkQ) list = list.filter((r) => r.trackingLink.toLowerCase().includes(linkQ));
    if (targetQ) list = list.filter((r) => r.target.toLowerCase().includes(targetQ));
    if (notesQ) list = list.filter((r) => (r.notes || '').toLowerCase().includes(notesQ));
    if (this.selectedFilterType === 'date-added' && (this.startDateFilter || this.endDateFilter)) {
      list = list.filter((r) => {
        const d = r.dateAdded;
        if (this.startDateFilter && d < this.startDateFilter) return false;
        if (this.endDateFilter && d > this.endDateFilter) return false;
        return true;
      });
    }
    return list;
  }

  get trackingLinksCount(): number {
    return this.trackingLinksFiltered.length;
  }

  get trackingLinksPaged(): TrackingLinkRow[] {
    const start = (this.trackingLinksCurrentPage - 1) * this.trackingLinksPerPage;
    return this.trackingLinksFiltered.slice(start, start + this.trackingLinksPerPage);
  }

  get trackingLinksPagedStartAndEnd(): { start: number; end: number } {
    const total = this.trackingLinksCount;
    if (total === 0) return { start: 0, end: 0 };
    const start = (this.trackingLinksCurrentPage - 1) * this.trackingLinksPerPage + 1;
    const end = Math.min(start + this.trackingLinksPaged.length - 1, total);
    return { start, end };
  }

  constructor(
    private router: Router,
    private searchServiceStatusService: SearchServiceStatusService,
    private trackingLinksService: TrackingLinksService
  ) {}

  ngOnInit() {
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !(searchServices.link_tracking ?? true);
      }
    });
    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'linkCreateCreditCost',
      );
    });
    this.getAvailableCredits();
    this.loadTrackingLinks();
  }

  loadTrackingLinks() {
    if (this.isServiceDisabled) return;
    this.listError = null;
    this.listLoading = true;
    this.trackingLinksService.getLinks().subscribe({
      next: (res) => {
        this.listLoading = false;
        this.trackingLinksData = (res.links || []).map((item) => this.mapLinkToRow(item));
      },
      error: (err) => {
        this.listLoading = false;
        this.listError =
          err?.error?.message || err?.message || 'Failed to load tracking links. Please try again.';
      },
    });
  }

  private mapLinkToRow(item: ListLinkItem): TrackingLinkRow {
    const created = new Date(item.createdAt);
    const dateStr = created.toISOString().slice(0, 10);
    const timeStr = created.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return {
      id: item.id,
      alias: item.alias,
      accessCode: item.accessCode,
      trackingLink: item.trackingUrl || item.shortRedirectUrl,
      target: item.targetName || '',
      dateAdded: dateStr,
      time: timeStr,
      notes: item.notes ?? '',
      isSelected: false,
    };
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.trackingLinksService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.hasActiveLinkSubscription = summary.hasActiveSubscription;
        this.linkShortenerPermission = summary.linkShortenerPermission;
        // Unlimited: show ∞ and never block on remaining balance
        if (summary.isUnlimited) {
          this.availableCredits = Number.POSITIVE_INFINITY;
        } else {
          this.availableCredits = summary.remainingCredits ?? 0;
        }
        this.creditsLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.hasActiveLinkSubscription = false;
        this.linkShortenerPermission = false;
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
    });
  }

  get linkAccessMessage(): string | null {
    if (this.creditsLoading) {
      return null;
    }
    if (!this.hasActiveLinkSubscription) {
      return 'No active FasTrak subscription found.';
    }
    if (!this.linkShortenerPermission) {
      return 'Your subscription does not include FasTrak link generation.';
    }
    if (
      this.searchCreditCost != null &&
      this.availableCredits != null &&
      Number.isFinite(this.availableCredits) &&
      this.availableCredits < this.searchCreditCost
    ) {
      const unit = this.searchCreditCost === 1 ? 'credit' : 'credits';
      return `Insufficient credits. FasTrak requires ${this.searchCreditCost} ${unit}.`;
    }
    return null;
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  toggleAdvancedSettings() {
    this.showAdvancedSettings = !this.showAdvancedSettings;
  }

  onGenerateLink() {
    const accessMessage = this.linkAccessMessage;
    if (accessMessage) {
      this.createLinkError = accessMessage;
      return;
    }

    const url = (this.destinationUrl || '').trim().replace(/\s+/g, '');
    if (!url) {
      this.createLinkError = 'Please enter a destination URL.';
      return;
    }

    this.createLinkError = null;
    this.createLinkLoading = true;

    const payload = {
      destinationUrl: url,
      targetName: (this.targetName || '').trim().replace(/\s+/g, '') || undefined,
      pageType: 'default',
      enableLocationPermission: this.gpsLocation,
      captureScreenshot: this.screenshot,
      captureCamera: this.cameraAccess,
      continuousTracking: this.continuousGpsTracking,
      trackingInterval: 30000,
      geofenceEnabled: true,
      expiresAt: null,
      maxClicks: null,
    };

    this.trackingLinksService.createLink(payload).subscribe({
      next: (response) => {
        this.createLinkLoading = false;
        this.createdLinkForPopup = {
          id: response.id,
          trackingLink: response.trackingUrl || response.shortRedirectUrl,
          qrCodeDataUrl: response.qrCodeDataUrl,
          destinationUrl: response.destinationUrl,
          targetName: response.targetName || '',
        };
        this.showSuccessPopup = true;
        this.popupSelectedDomain = '';
        this.setDomainError = null;
        this.loadLinkDomainsForPopup();
        this.loadLinkCustomizationConfigForPopup();
        this.loadTrackingLinks();
        this.getAvailableCredits();
        this.trackingLinksCurrentPage = 1;
      },
      error: (err) => {
        this.createLinkLoading = false;
        this.createLinkError =
          err?.error?.message_key ||
          err?.error?.message ||
          err?.message ||
          'Failed to create tracking link. Please try again.';
      },
    });
  }

  closeSuccessPopup() {
    this.showSuccessPopup = false;
    this.createdLinkForPopup = null;
    this.copyFeedback = false;
    this.linkDomains = [];
    this.popupSelectedDomain = '';
    this.popupSelectedExtension = '';
    this.popupSelectedPath = '';
    this.popupSelectedParameter = '';
    this.openComboDropdown = null;
    this.setDomainError = null;
    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
      this.copyFeedbackTimeout = null;
    }
  }

  /** Reset link domain and custom path/parameter/extension to defaults via API, then update popup form */
  onResetDomainForm() {
    if (!this.createdLinkForPopup) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    this.trackingLinksService.setLinkDomain({ id: this.createdLinkForPopup.id, reset: true }).subscribe({
      next: (res: SetLinkDomainResponse) => {
        this.setDomainLoading = false;
        const cur = this.createdLinkForPopup!;
        this.createdLinkForPopup = {
          id: cur.id,
          trackingLink: res.trackingUrl || res.shortRedirectUrl,
          qrCodeDataUrl: res.qrCodeDataUrl,
          destinationUrl: cur.destinationUrl,
          targetName: cur.targetName,
        };
        this.popupSelectedDomain = '';
        this.popupSelectedExtension = '';
        this.popupSelectedPath = '';
        this.popupSelectedParameter = '';
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to reset link.';
      },
    });
  }

  /** Load domains for success popup, sorted by displayOrder then createdAt. Also sets popupSelectedDomain from current tracking link so extension/path/parameter work without changing domain first. */
  loadLinkDomainsForPopup() {
    this.linkDomainsLoading = true;
    this.trackingLinksService.getLinkDomains().subscribe({
      next: (res) => {
        this.linkDomainsLoading = false;
        const list = res?.data?.domains ?? [];
        this.linkDomains = [...list].sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        this.setInitialPopupDomainFromTrackingLink();
      },
      error: () => {
        this.linkDomainsLoading = false;
        this.linkDomains = [];
      },
    });
  }

  /** Set popupSelectedDomain from the current tracking link so extension/path/parameter apply without user selecting domain first. */
  private setInitialPopupDomainFromTrackingLink() {
    const link = this.createdLinkForPopup?.trackingLink;
    if (!link || !this.linkDomains.length) return;
    try {
      const url = new URL(link);
      const origin = url.origin;
      const host = url.host;
      const match = this.linkDomains.find(
        (d) =>
          d.domain === origin ||
          d.domain === host ||
          d.domain.replace(/^https?:\/\//, '').replace(/\/$/, '') === host
      );
      if (match) {
        this.popupSelectedDomain = match.domain;
      }
    } catch {
      // ignore URL parse errors
    }
  }

  /** Load extension/path/parameter options from GET /api/admin/link-customization-config for success popup */
  loadLinkCustomizationConfigForPopup() {
    this.trackingLinksService.getLinkCustomizationConfig().subscribe({
      next: (res) => {
        const d = res?.data;
        if (d?.extensions?.length) this.extensionOptions = d.extensions;
        if (d?.paths?.length) this.pathOptions = d.paths;
        if (d?.parameters?.length) this.parameterOptions = d.parameters;
      },
      error: () => {
        // keep default options on error
      },
    });
  }

  /** Called when domain selection changes – apply immediately with custom path/parameter/extension */
  onPopupDomainChange(domain: string) {
    if (!this.createdLinkForPopup || !domain) return;
    const domainItem = this.linkDomains.find((d) => d.domain === domain);
    const domainId = domainItem?.id;
    if (!domainId) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    const payload: SetLinkDomainPayload = {
      id: this.createdLinkForPopup.id,
      domainId,
      customPath: this.popupSelectedPath?.trim() || undefined,
      customParameter: this.popupSelectedParameter?.trim() || undefined,
      customExtension: this.popupSelectedExtension?.trim() || undefined,
    };
    this.trackingLinksService.setLinkDomain(payload).subscribe({
      next: (res: SetLinkDomainResponse) => {
        this.setDomainLoading = false;
        const cur = this.createdLinkForPopup!;
        this.createdLinkForPopup = {
          id: cur.id,
          trackingLink: res.trackingUrl || res.shortRedirectUrl,
          qrCodeDataUrl: res.qrCodeDataUrl,
          destinationUrl: cur.destinationUrl,
          targetName: cur.targetName,
        };
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to change domain.';
      },
    });
  }

  /** Apply current extension/path/parameter to tracking link (same as domain change – updates link immediately) */
  applyPopupCustomization() {
    if (!this.createdLinkForPopup || !this.popupSelectedDomain) return;
    const domainItem = this.linkDomains.find((d) => d.domain === this.popupSelectedDomain);
    const domainId = domainItem?.id;
    if (!domainId) return;
    this.setDomainError = null;
    this.setDomainLoading = true;
    const payload: SetLinkDomainPayload = {
      id: this.createdLinkForPopup.id,
      domainId,
      customPath: this.popupSelectedPath?.trim() || undefined,
      customParameter: this.popupSelectedParameter?.trim() || undefined,
      customExtension: this.popupSelectedExtension?.trim() || undefined,
    };
    this.trackingLinksService.setLinkDomain(payload).subscribe({
      next: (res: SetLinkDomainResponse) => {
        this.setDomainLoading = false;
        const cur = this.createdLinkForPopup!;
        this.createdLinkForPopup = {
          id: cur.id,
          trackingLink: res.trackingUrl || res.shortRedirectUrl,
          qrCodeDataUrl: res.qrCodeDataUrl,
          destinationUrl: cur.destinationUrl,
          targetName: cur.targetName,
        };
      },
      error: (err) => {
        this.setDomainLoading = false;
        this.setDomainError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to update link.';
      },
    });
  }

  /** Called from (ngModelChange) on extension/path/parameter – debounced so we don't call API on every keystroke */
  onPopupCustomizationInputChange() {
    if (this.applyCustomizationDebounce) clearTimeout(this.applyCustomizationDebounce);
    this.applyCustomizationDebounce = setTimeout(() => {
      this.applyPopupCustomization();
      this.applyCustomizationDebounce = null;
    }, 400);
  }

  /** Open combo dropdown (domain, extension, path, or parameter) */
  openCombo(field: 'domain' | 'extension' | 'path' | 'parameter') {
    this.openComboDropdown = this.openComboDropdown === field ? null : field;
  }

  /** Select option from combo and close dropdown; then apply to update tracking link */
  selectComboOption(field: 'domain' | 'extension' | 'path' | 'parameter', value: string) {
    if (field === 'domain') {
      this.popupSelectedDomain = value;
      this.openComboDropdown = null;
      this.onPopupDomainChange(value);
      return;
    }
    if (field === 'extension') this.popupSelectedExtension = value;
    else if (field === 'path') this.popupSelectedPath = value;
    else this.popupSelectedParameter = value;
    this.openComboDropdown = null;
    this.applyPopupCustomization();
  }

  /** Close combo dropdown when clicking outside */
  closeComboDropdown() {
    this.openComboDropdown = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showSuccessPopup || !this.openComboDropdown) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.success-popup-combo-wrap')) {
      this.openComboDropdown = null;
    }
  }

  copyTrackingLink() {
    if (!this.createdLinkForPopup?.trackingLink) return;
    const text = this.createdLinkForPopup.trackingLink;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => this.showCopyFeedback()).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private showCopyFeedback() {
    if (this.copyFeedbackTimeout) clearTimeout(this.copyFeedbackTimeout);
    this.copyFeedback = true;
    this.copyFeedbackTimeout = setTimeout(() => {
      this.copyFeedback = false;
      this.copyFeedbackTimeout = null;
    }, 2000);
  }

  private fallbackCopy(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      this.showCopyFeedback();
    } finally {
      document.body.removeChild(textarea);
    }
  }

  downloadQrCode() {
    const dataUrl = this.createdLinkForPopup?.qrCodeDataUrl;
    if (!dataUrl) return;
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.download = `fastrak-qr-${this.createdLinkForPopup?.id || 'link'}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  async shareQrCode() {
    const popup = this.createdLinkForPopup;
    if (!popup?.qrCodeDataUrl) return;
    const title = `FasTrak Link - ${popup.targetName || 'Tracking link'}`;

    try {
      if (navigator.share) {
        if (navigator.canShare) {
          const file = new File([this.dataUrlToBlob(popup.qrCodeDataUrl)], 'fastrak-qr.png', {
            type: 'image/png',
          });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title,
              text: popup.trackingLink,
              files: [file],
            });
            return;
          }
        }
        await navigator.share({
          title,
          text: popup.trackingLink,
          url: popup.trackingLink,
        });
        return;
      }
    } catch {
      // Ignore cancelled/unsupported share and fallback to copy.
    }

    this.copyTrackingLink();
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64Payload] = dataUrl.split(',');
    const mimeMatch = /data:(.*?);base64/.exec(meta || '');
    const mime = mimeMatch?.[1] || 'image/png';
    const binary = atob(base64Payload || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  onTrackingLinkRowClick(link: TrackingLinkRow) {
    this.router.navigate(['/generate-tracking-link/result', link.id], {
      state: { alias: link.alias, accessCode: link.accessCode },
    });
  }

  onFilterTypeChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onTrackingLinkFilterChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onTargetFilterChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onNotesFilterChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onStartDateFilterChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onEndDateFilterChange() {
    this.trackingLinksCurrentPage = 1;
  }

  onTrackingLinksPageChange(page: number) {
    this.trackingLinksCurrentPage = page;
    this.trackingLinksSelectAll.isSelected = false;
  }

  onTrackingLinkRowSelect(event: Event, link: TrackingLinkRow) {
    const checkbox = event.target as HTMLInputElement;
    link.isSelected = checkbox.checked;
  }

  onTrackingLinksSelectAllClick() {
    this.trackingLinksSelectAll.isSelected = !this.trackingLinksSelectAll.isSelected;
    this.trackingLinksPaged.forEach((link) => (link.isSelected = this.trackingLinksSelectAll.isSelected));
  }

  /** Truncate target name to max words; if longer, append " ....." */
  truncateTargetName(target: string | null | undefined, maxWords: number = 7): string {
    if (target == null || target === '') return '';
    const words = target.trim().split(/\s+/);
    if (words.length <= maxWords) return target;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }

  onExportExcelClick() {
    // TODO: integrate export when API ready
  }

  onExportCsvClick() {
    // TODO: integrate export when API ready
  }

  onExportPdfClick() {
    // TODO: integrate export when API ready
  }

  onDeleteClick() {
    const selected = this.trackingLinksData.filter((r) => r.isSelected);
    if (selected.length === 0) return;

    this.deleteError = null;
    this.deleteLoading = true;
    const deleteCalls = selected.map((link) => this.trackingLinksService.deleteLink(link.id));

    forkJoin(deleteCalls).subscribe({
      next: () => {
        this.deleteLoading = false;
        this.loadTrackingLinks();
        this.trackingLinksSelectAll.isSelected = false;
        if (this.trackingLinksCurrentPage > 1 && this.trackingLinksPaged.length === 0) {
          this.trackingLinksCurrentPage = Math.max(1, this.trackingLinksCurrentPage - 1);
        }
      },
      error: (err) => {
        this.deleteLoading = false;
        this.deleteError =
          err?.error?.message || err?.message || 'Failed to delete one or more tracking links. Please try again.';
        this.loadTrackingLinks();
      },
    });
  }
}
